import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Phone, 
  MessageCircle, 
  Clock, 
  Check, 
  CheckCheck,
  X, 
  TriangleAlert,
  ArrowRight, 
  Play, 
  Pause, 
  Volume2, 
  Star,
  Zap,
  Shield,
  Home,
  ChevronLeft,
  Brain
} from 'lucide-react';
import { Button } from '../../components/ui/button';

// Animated section component
const AnimatedSection = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10% 0px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ 
        duration: 0.9, 
        delay: delay,
        ease: [0.22, 1, 0.36, 1]
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Side-by-side audio player comparison component
const AudioComparison = ({ scenario, ourAudio, theirAudio, delay = 0 }) => {
  const [ourPlaying, setOurPlaying] = useState(false);
  const [theirPlaying, setTheirPlaying] = useState(false);
  const [ourProgress, setOurProgress] = useState(0);
  const [theirProgress, setTheirProgress] = useState(0);
  const [ourDuration, setOurDuration] = useState(0);
  const [theirDuration, setTheirDuration] = useState(0);

  const ourAudioRef = useRef(null);
  const theirAudioRef = useRef(null);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10% 0px" });

  // Format time in MM:SS
  const formatTime = (time) => {
    if (isNaN(time)) return "00:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Stop other audio when one starts playing
  const handleOurPlay = () => {
    if (theirPlaying) {
      theirAudioRef.current?.pause();
      setTheirPlaying(false);
    }

    if (ourPlaying) {
      ourAudioRef.current?.pause();
      setOurPlaying(false);
    } else {
      ourAudioRef.current?.play();
      setOurPlaying(true);
    }
  };

  const handleTheirPlay = () => {
    if (ourPlaying) {
      ourAudioRef.current?.pause();
      setOurPlaying(false);
    }

    if (theirPlaying) {
      theirAudioRef.current?.pause();
      setTheirPlaying(false);
    } else {
      theirAudioRef.current?.play();
      setTheirPlaying(true);
    }
  };

  // Handle seeking when clicking on progress bar
  const handleOurSeek = (e) => {
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * ourDuration;

    if (ourAudioRef.current && !isNaN(newTime)) {
      ourAudioRef.current.currentTime = newTime;
    }
  };

  const handleTheirSeek = (e) => {
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * theirDuration;

    if (theirAudioRef.current && !isNaN(newTime)) {
      theirAudioRef.current.currentTime = newTime;
    }
  };

  // Handle audio events
  useEffect(() => {
    const ourAudio = ourAudioRef.current;
    const theirAudio = theirAudioRef.current;

    const handleOurLoadedMetadata = () => setOurDuration(ourAudio.duration);
    const handleTheirLoadedMetadata = () => setTheirDuration(theirAudio.duration);

    const handleOurTimeUpdate = () => setOurProgress(ourAudio.currentTime);
    const handleTheirTimeUpdate = () => setTheirProgress(theirAudio.currentTime);

    const handleOurEnded = () => setOurPlaying(false);
    const handleTheirEnded = () => setTheirPlaying(false);

    if (ourAudio) {
      ourAudio.addEventListener('loadedmetadata', handleOurLoadedMetadata);
      ourAudio.addEventListener('timeupdate', handleOurTimeUpdate);
      ourAudio.addEventListener('ended', handleOurEnded);
    }

    if (theirAudio) {
      theirAudio.addEventListener('loadedmetadata', handleTheirLoadedMetadata);
      theirAudio.addEventListener('timeupdate', handleTheirTimeUpdate);
      theirAudio.addEventListener('ended', handleTheirEnded);
    }

    return () => {
      if (ourAudio) {
        ourAudio.removeEventListener('loadedmetadata', handleOurLoadedMetadata);
        ourAudio.removeEventListener('timeupdate', handleOurTimeUpdate);
        ourAudio.removeEventListener('ended', handleOurEnded);
      }
      if (theirAudio) {
        theirAudio.removeEventListener('loadedmetadata', handleTheirLoadedMetadata);
        theirAudio.removeEventListener('timeupdate', handleTheirTimeUpdate);
        theirAudio.removeEventListener('ended', handleTheirEnded);
      }
    };
  }, []);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ 
        duration: 0.9, 
        delay: isInView ? delay : 0,
        ease: [0.22, 1, 0.36, 1]
      }}
      className="mb-16 sm:mb-20"
    >
      {/* Scenario Header */}
      <div className="text-center mb-8 sm:mb-12">
        <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 tracking-tight">
          {scenario.title}
        </h3>
        <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
          {scenario.description}
        </p>
      </div>

      {/* Side-by-side comparison */}
      <div className="grid md:grid-cols-2 gap-6 sm:gap-8 mx-auto" style={{ maxWidth: '66rem' }}>
        {/* Their Solution */}
        <div className="order-1 md:order-1">
          <div className="bg-red-50 rounded-xl p-6 sm:p-8 border border-red-100 relative">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <X className="w-6 h-6 text-red-600" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">{scenario.competitor}</h4>
              <p className="text-sm text-red-600 font-medium">What you get with them</p>
            </div>

            <div className="bg-white rounded-lg p-4 sm:p-5 mb-4">
              <div className="flex items-center mb-3">
                <button 
                  onClick={handleTheirPlay}
                  className="w-12 h-12 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center mr-4 transition-colors duration-200"
                >
                  {theirPlaying ? 
                    <Pause className="w-5 h-5 text-white" /> : 
                    <Play className="w-5 h-5 text-white ml-0.5" />
                  }
                </button>

                <div className="flex-grow">
                  <div 
                    className="h-2 bg-red-100 rounded-full overflow-hidden mb-2 cursor-pointer hover:bg-red-200 transition-colors duration-200"
                    onClick={handleTheirSeek}
                  >
                    <div 
                      className="h-full bg-red-500 rounded-full transition-all duration-100 pointer-events-none"
                      style={{ width: `${theirDuration ? (theirProgress / theirDuration) * 100 : 0}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{formatTime(theirProgress)}</span>
                    <span>{formatTime(theirDuration)}</span>
                  </div>
                </div>
              </div>
              <audio ref={theirAudioRef} src={theirAudio} preload="metadata" />

              {/* Timestamp proof */}
              <div className="text-xs text-gray-500 text-center mt-2 border-t border-gray-100 pt-2">
                <div>{scenario.theirTimestamp}</div>
                <div className="mt-1 font-medium">{scenario.theirDate}</div>
              </div>
            </div>

            {/* Issues with their solution */}
            <div className="space-y-2">
              {scenario.theirIssues.map((issue, index) => (
                <div key={index} className="flex items-start">
                  {index === 4 ? (
                    <TriangleAlert className="w-4 h-4 text-red-500 flex-shrink-0 mr-2 mt-0.5" />
                  ) : (
                    <X className="w-4 h-4 text-red-500 flex-shrink-0 mr-2 mt-0.5" />
                  )}
                  <span className="text-sm text-gray-600">{issue}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Our Solution */}
        <div className="order-2 md:order-2">
          <div className="bg-green-50 rounded-xl p-6 sm:p-8 border-2 border-green-200 relative shadow-sm">
            {/* Winner badge */}
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <div className="bg-green-600 text-white text-xs font-bold px-4 py-1 rounded-full flex items-center">
                <Star className="w-3 h-3 mr-1" />
                CLEAR WINNER
              </div>
            </div>

            <div className="text-center mb-6 mt-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">The Savants</h4>
              <p className="text-sm text-green-600 font-medium">What you get with us</p>
            </div>

            <div className="bg-white rounded-lg p-4 sm:p-5 mb-4 shadow-sm">
              <div className="flex items-center mb-3">
                <button 
                  onClick={handleOurPlay}
                  className="w-12 h-12 rounded-full bg-green-600 hover:bg-green-700 flex items-center justify-center mr-4 transition-colors duration-200"
                >
                  {ourPlaying ? 
                    <Pause className="w-5 h-5 text-white" /> : 
                    <Play className="w-5 h-5 text-white ml-0.5" />
                  }
                </button>

                <div className="flex-grow">
                  <div 
                    className="h-2 bg-green-100 rounded-full overflow-hidden mb-2 cursor-pointer hover:bg-green-200 transition-colors duration-200"
                    onClick={handleOurSeek}
                  >
                    <div 
                      className="h-full bg-green-500 rounded-full transition-all duration-100 pointer-events-none"
                      style={{ width: `${ourDuration ? (ourProgress / ourDuration) * 100 : 0}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{formatTime(ourProgress)}</span>
                    <span>{formatTime(ourDuration)}</span>
                  </div>
                </div>
              </div>
              <audio ref={ourAudioRef} src={ourAudio} preload="metadata" />

              {/* Timestamp proof */}
              <div className="text-xs text-gray-500 text-center mt-2 border-t border-gray-100 pt-2">
                <div>{scenario.ourTimestamp}</div>
                <div className="mt-1 font-medium">{scenario.ourDate}</div>
              </div>
            </div>

            {/* Benefits of our solution */}
            <div className="space-y-2">
              {scenario.ourBenefits.map((benefit, index) => (
                <div key={index} className="flex items-start">
                  {index === 4 ? (
                    <CheckCheck className="w-4 h-4 text-green-500 flex-shrink-0 mr-2 mt-0.5" />
                  ) : (
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0 mr-2 mt-0.5" />
                  )}
                  <span className="text-sm text-gray-600">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Helper function to render star rating
const StarRating = ({ rating, size = "w-4 h-4" }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    const isFilled = i <= rating;
    const isPartial = i > rating && i - 1 < rating;
    const fillPercentage = isPartial ? ((rating - (i - 1)) * 100) : 100;

    stars.push(
      <div key={i} className="relative inline-block">
        {/* Background star (empty) */}
        <Star className={`${size} text-gray-300`} />
        {/* Filled portion */}
        {(isFilled || isPartial) && (
          <div 
            className="absolute top-0 left-0 overflow-hidden"
            style={{ width: `${isFilled ? 100 : fillPercentage}%` }}
          >
            <Star className={`${size} text-yellow-400 fill-current`} />
          </div>
        )}
      </div>
    );
  }
  return <div className="flex items-center justify-center gap-0.5">{stars}</div>;
};

// Results comparison component
const ResultsComparison = () => {
  const metrics = [
    {
      label: "Customer Satisfaction",
      us: 4.8,
      them: 2.6,
      unit: " stars",
      is_stars: true,
      subtext: "Average rating of real Google reviews mentioning the word \"AI\"",
      us_detail: "",
      them_detail: ""
    },
    {
      label: "Success Rate",
      us: 91,
      them: 46,
      unit: "%",
      subtext: "Calls handled successfully without transferring them to you",
      us_detail: "",
      them_detail: ""
    },
    {
      label: "Response Speed",
      us: 1.2,
      them: 5.1,
      unit: " seconds",
      lower_is_better: true,
      subtext: "Silence time before responding",
      us_detail: "",
      them_detail: ""
    },
    {
      label: "Languages Supported",
      us: "5+",
      them: "1 (barely)",
      unit: "",
      is_text: true,
      subtext: "Languages the AI can speak",
      us_detail: "",
      them_detail: ""
    }
  ];

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
      {metrics.map((metric, index) => (
        <AnimatedSection key={metric.label} delay={0.1 * index}>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center h-full flex flex-col">
            <h4 className="text-lg font-semibold text-gray-700 mb-2">{metric.label}</h4>
            <p className="text-xs text-gray-500 mb-4 flex-shrink-0">{metric.subtext}</p>

                        <div className="flex-grow flex flex-col justify-center">
              {/* Regular two-way comparison */}
              <>
                {/* Our score */}
                <div className="mb-3">
                  <div className="text-2xl font-bold text-green-600 mb-1">
                    {metric.is_stars ? (
                      <div className="flex items-center justify-center gap-2">
                        <span>{metric.us}</span>
                        <StarRating rating={metric.us} size="w-5 h-5" />
                      </div>
                    ) : metric.is_text ? (
                      <span>{metric.us}</span>
                    ) : (
                      <span>{metric.us}{metric.unit}</span>
                    )}
                  </div>
                  {metric.us_detail && (
                    <div className="text-xs text-gray-600 mb-1">
                      {metric.us_detail}
                    </div>
                  )}
                  <div className="text-xs text-gray-500">The Savants</div>
                </div>

                {/* VS */}
                <div className="text-xs text-gray-400 font-medium mb-3">VS</div>

                {/* Their score */}
                <div className="mb-3">
                  <div className="text-xl font-semibold text-red-500 mb-1">
                    {metric.is_stars ? (
                      <div className="flex items-center justify-center gap-2">
                        <span>{metric.them}</span>
                        <StarRating rating={metric.them} size="w-4 h-4" />
                      </div>
                    ) : metric.is_text ? (
                      <span>{metric.them}</span>
                    ) : (
                      <span>{metric.them}{metric.unit}</span>
                    )}
                  </div>
                  {metric.them_detail && (
                    <div className="text-xs text-gray-600 mb-1">
                      {metric.them_detail}
                    </div>
                  )}
                  <div className="text-xs text-gray-500">Pennies "Assist"</div>
                </div>
              </>
            </div>

            {/* Improvement indicator */}
            <div className="mt-4 pt-3 border-t border-gray-100">
              <div className="text-xs font-medium text-green-600">
                {metric.is_stars ? (
                  "85% higher rating"
                ) : metric.is_text ? (
                  "5x more languages"
                ) : metric.lower_is_better ? (
                  "4x faster"
                ) : (
                  "2x better"
                )}
              </div>
            </div>
          </div>
        </AnimatedSection>
      ))}
    </div>
  );
};

const CompetitorComparison = () => {
  const navigate = useNavigate();

  // Real laundromat scenarios that would resonate with a 60-year-old owner
  const scenarios = [
    {
      title: "Losing a Customer vs. Capturing an Order",
      description: "A customer wants to schedule a pickup and delivery order. This is a direct test of losing money vs. making it.",
      competitor: "Pennies \"Assist\"",
      ourAudio: "https://storage.thesavants.ai/storage/v1/object/public/AudioForWebsite//Manara-Wash-call.mp3.m4a",
      theirAudio: "https://storage.thesavants.ai/storage/v1/object/public/AudioForWebsite//PenniesCall1.mp3",
      ourTimestamp: "Real customer call • Manara Wash, Mt. Kisco, NY",
      ourDate: "May 28, 2025 at 8:48 AM",
      theirTimestamp: "Perfect conditions demo • Pennies best-case scenario",
      theirDate: "June 11th, 2025 at 12:36pm",
      ourBenefits: [
        "Greets John by name - remembers he's a returning customer",
        "Confirms his usual pickup details instantly",
        "Books the order in under 30 seconds",
        "Customer hangs up satisfied, order complete"
      ],
      theirIssues: [
        "Admits it has no memory of previous customers",
        "Customer waits 5 seconds in silence - twice",
        "Tries to transfer instead of taking the order",
        "Customer hangs up frustrated - order lost"
      ]
    },
    {
      title: "A Lot of Work vs. A Little Magic",
      description: "Customer asks for directions to your laundromat. Watch how we turn a simple question into a delightful experience.",
      competitor: "Pennies \"Assist\"",
      ourAudio: "https://storage.thesavants.ai/storage/v1/object/public/AudioForWebsite//Columbus-Express-Laundry-call.mp3.m4a",
      theirAudio: "https://storage.thesavants.ai/storage/v1/object/public/AudioForWebsite//PenniesCall2.mp3",
      ourTimestamp: "Real customer call • Columbus Express Laundry, Columbus, OH",
      ourDate: "May 21, 2025 at 5:43 PM",
      theirTimestamp: "Perfect conditions demo • Pennies best-case scenario",
      theirDate: "June 11th, 2025 at 12:38pm",
      ourBenefits: [
        "Proactively offers to send directions to customer's phone",
        "Instantly texts directions when customer says 'Yep'",
        "Provides clear hours and address without confusion",
        "Customer gets what they need and ends call satisfied"
      ],
      theirIssues: [
        "Customer has to ask twice for the address",
        "Admits they don't have text messaging for directions",
        "Forces customer to write down address by hand",
        "Customer hangs up mid-sentence in frustration"
      ]
    },
    {
      title: "An Answering Machine vs. A Helpful Assistant",
      description: "Customer asks about specialty services. Our AI doesn't just answer—it takes action when it doesn't know something.",
      competitor: "Pennies \"Assist\"",
      ourAudio: "https://storage.thesavants.ai/storage/v1/object/public/AudioForWebsite//Super-Clean-Laundry-call.mp3.m4a",
      theirAudio: "https://storage.thesavants.ai/storage/v1/object/public/AudioForWebsite//PenniesCall3.mp3",
      ourTimestamp: "Real customer call • Super Clean Laundry, Cottonwood, AZ",
      ourDate: "May 27, 2025 at 10:42 PM",
      theirTimestamp: "Perfect conditions demo • Pennies best-case scenario",
      theirDate: "June 11th, 2025 at 12:41pm",
      ourBenefits: [
        "Offers to leave a note when it doesn't know something",
        "Continues the conversation instead of transferring",
        "Provides helpful details about hours and services",
        "Customer ends impressed: 'Wow that's pretty impressive!'"
      ],
      theirIssues: [
        "Transfers customer despite them begging 'Don't transfer me!'",
        "Dumps customer into another automated system",
        "Customer gets cut off mid-sentence",
        "Completely abandons the potential new customer"
      ]
    },
    {
      title: "Excluding Customers vs. Welcoming Everyone",
      description: "Spanish-speaking customer needs help with a broken machine. Watch how language support can either welcome customers or drive them away.",
      competitor: "Pennies \"Assist\"",
      ourAudio: "https://storage.thesavants.ai/storage/v1/object/public/AudioForWebsite//Washouse-Laundry-call.mp3",
      theirAudio: "https://storage.thesavants.ai/storage/v1/object/public/AudioForWebsite//PenniesCall4.m4a",
      ourTimestamp: "Real customer call • Washouse Laundry, Southwest FL",
      ourDate: "May 19, 2025 at 6:53 AM",
      theirTimestamp: "Perfect conditions demo • Pennies best-case scenario",
      theirDate: "June 12th, 2025 at 9:38am",
      ourBenefits: [
        "Seamlessly switches to fluent Spanish and stays there",
        "Gets machine number, location, customer name and phone",
        "Confirms all details back to ensure accuracy",
        "Creates a complete service ticket before ending call",
        "Also supports Portuguese, French, Creole, German & Mandarin"
      ],
      theirIssues: [
        "Randomly switches between Spanish and English mid-sentence",
        "Promises 'someone will come' without getting location details",
        "Never asks which machine or gets contact information",
        "Customer has no way to follow up on the 'help' promised",
        "Only supports this awkward version of Spanish - no other languages"
      ]
    }
  ];

  useEffect(() => {
    document.title = "Savant vs The Competition - See The Difference";
  }, []);

  const handleGetStarted = () => {
    navigate('/signup');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <nav className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <button 
              onClick={() => navigate('/')}
              className="mr-4 p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex items-center">
              <div className="relative">
                <span className="font-medium text-lg text-gray-900">savant</span>
                <div 
                  className="absolute top-0.5 -right-1.5 bg-blue-600" 
                  style={{ 
                    width: '4px', 
                    height: '4px', 
                    borderRadius: '50%',
                    transform: 'scale(1.2)',
                    transformOrigin: 'center'
                  }}
                ></div>
              </div>
            </div>
          </div>
          <Button
            onClick={handleGetStarted}
            className="bg-blue-600 hover:bg-blue-700"
          >
            90 Days Free
          </Button>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <AnimatedSection>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 tracking-tight leading-tight">
              Hear The Difference
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 mb-8 leading-relaxed max-w-2xl mx-auto">
              Same questions. Same scenarios. Completely different results. 
              Listen to how Savant handles your customers versus our biggest competitor (Pennies).
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Confidence Statement */}
      <section className="py-8 px-4 sm:px-6 bg-blue-50 border-y border-blue-100">
        <div className="max-w-4xl mx-auto">
          <AnimatedSection delay={0.1}>
            <div className="text-center">
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-lg text-sm font-medium mb-4 border border-blue-100">
                <Shield className="w-4 h-4 mr-2" />
                The Ultimate Test
              </div>
              <p className="text-base text-gray-700 max-w-2xl mx-auto leading-relaxed">
                <strong>Why real calls matter:</strong> Any company can make a demo sound perfect. 
                We're confident enough to show you the real thing.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Audio Comparisons */}
      <section className="py-8 sm:py-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          {scenarios.map((scenario, index) => (
            <AudioComparison 
              key={index}
              scenario={scenario}
              ourAudio={scenario.ourAudio}
              theirAudio={scenario.theirAudio}
              delay={0.2 * index}
            />
          ))}
        </div>
      </section>

      {/* Results Section */}
      <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 tracking-tight">
              The Numbers Don't Lie
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Based on real customer calls vs. their demo tests
            </p>
          </AnimatedSection>

          <ResultsComparison />
        </div>
      </section>

      {/* Why We're Better Section */}
      <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 tracking-tight">
              Why Savant Wins Every Time
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Real customers. Real stakes. Real results. While they perfect demos, we perfect service.
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8 sm:gap-10">
            <AnimatedSection delay={0.1}>
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Zap className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Built for the Real World, Not Demos</h3>
                <p className="text-gray-600 leading-relaxed">
                  Generic chatbots break when Mrs. Chen calls about her wedding dress emergency at 9 PM. Savant thrives.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Brain className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Reads the Room</h3>
                <p className="text-gray-600 leading-relaxed">
                  Emergency vs. routine. New customer vs. regular. Human vs. robot. Savant knows the difference.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.3}>
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Star className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">It's You, But Better</h3>
                <p className="text-gray-600 leading-relaxed">
                  Your customers can't tell the difference. Except Savant never has a bad day.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <AnimatedSection>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6 tracking-tight">
              Your customers deserve dollars, not Cents.
            </h2>
            <p className="text-base sm:text-lg text-blue-100 mb-8 leading-relaxed max-w-2xl mx-auto">
              Your customers deserve better than busy signals and voicemail.<br />
              Give it to them.
            </p>
            <div className="space-y-4">
              <Button
                size="lg"
                onClick={handleGetStarted}
                className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg"
              >
                Get Savant Today
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <div className="text-sm text-blue-100">
                90 days free. Hear the difference yourself.
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default CompetitorComparison; 